import dynamic from 'next/dynamic';
import React, { useState, useRef } from 'react';
import {
  array, func, string, object, number, bool,
} from 'prop-types';
import { Col, Slider as InputSlider } from 'antd';

import { useSunnei } from 'context/productSheet';
import { SunneiPicker } from 'components/sunnei/sunneiPicker';
import styles from './style.module.css';

const Slider = dynamic(() => import('react-slick'));
const ProgressiveBlurredImg = dynamic(() => import('components/base/image'));
const MobileThreeDViewer = dynamic(() => import('components/tryOn/mobileThreeD').then((mod) => mod.MobileThreeDButton));

function Image({
  imgSrc, alt, grid, onClick, index,
}) {
  const { isVideo } = imgSrc;

  return (
    <Col
      className={styles.imagesGrid}
      onClick={onClick ? () => onClick(imgSrc, index) : null}
      {...grid}
    >
      {
        isVideo ?
        <video
          className='lazy'
          // autoPlay
          loop
          title={`Video of ${alt}`}
          muted
          playsInline
          poster={imgSrc?.poster}
          dangerouslySetInnerHTML={{ __html: imgSrc?.sources }}
        /> :
        <ProgressiveBlurredImg
          lowQualitySrc={imgSrc.xs}
          highQualitySrc={imgSrc}
          alt={alt}
          sizes="(min-width: 992px) 33vw, (min-width: 768px) 70vw, 50vw"
          index={index}
          initLoaded={true}
        />
      }
    </Col>
  );
}
Image.defaultProps = {
  giftCardText: '',
};
Image.propTypes = {
  imgSrc: object,
  grid: object,
  alt: string.isRequired,
  onClick: func,
  index: number,
};
function ImagesGrid({
  images, grid, alt, onClick, has3d, isMobile, code: sku,
}) {
  const { hasSunneiConfig } = useSunnei();
  const [ slideIndex, setSlideIndex ] = useState(0);
  const [ changeSlideIndex, setChangeSlideIndex ] = useState(0);
  const sliderRef = useRef();

  const { code, brand } = alt;

  const settings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    initialSlide: 0,
    swipeToSlide: true,
    easing: false,
    lazyLoad: false,
    draggable: true,
    swipe: true,
    useTransform: false,
    arrows: false,
    beforeChange: (current, next) => setSlideIndex(next),
    afterChange: (current) => setChangeSlideIndex(current),
  };

  const ImageComponents = images.reduce((acc, imgSrc, index) => {
    if (index === 0 && hasSunneiConfig) {
      acc.push(<SunneiPicker key="sunneiPicker" />);
    }
    if (index === 2 && has3d && isMobile) {
      acc.push(<MobileThreeDViewer key="mobileThreeD" code={sku} poster={images?.[1].poster} />);
    }

    acc.push(<Image
      key={`ig-${index}`}
      onClick={onClick && slideIndex === changeSlideIndex ? () => onClick(imgSrc, index) : null}
      grid={grid}
      imgSrc={imgSrc}
      alt={imgSrc?.altText || `${code} - ${brand}`}
      index={index}
      loading='lazy'
    />);
    return acc;
  }, []);

  return (
    <>
      <div key="imageSlider" className={styles.imageSliderWrapper}>
        <Slider {...settings} className={styles.productsSlider} ref={sliderRef}>
          {ImageComponents}
        </Slider>
        <InputSlider
          className={styles.scrollSlider}
          min={0}
          max={images.length}
          value={slideIndex}
          tipFormatter={null}
          handleStyle={{ width: `${100 / images.length}%`, transform: 'translateX(0%)' }}
        />
        <p className={styles.currentSlideText}>{`${ImageComponents.length - (slideIndex + 1) < 1 ? ImageComponents.length : slideIndex + 1} / ${ImageComponents.length}`}</p>
      </div>
      <div key="imageMap" className={`${styles.imageMapWrapper}`}>
        {
          images.map((imgSrc, index) => (
            <Image
              key={`ig-${index}`}
              onClick={onClick ? () => onClick(imgSrc, index) : null}
              grid={grid}
              imgSrc={imgSrc}
              alt={imgSrc?.altText || alt}
              index={index}
            />
          ))
        }
      </div>
    </>
  );
}

ImagesGrid.defaultProps = {
  images: [],
  grid: {
    xs: { span: 12 },
    sm: { span: 12 },
    md: { span: 24 },
    lg: { span: 12 },
    xl: { span: 12 },
    xxl: { span: 12 },
  },
  giftCardText: '',
  isMobile: false,
  has3d: false,
};

ImagesGrid.propTypes = {
  images: array,
  grid: object,
  alt: string.isRequired,
  onClick: func,
  isMobile: bool,
  has3d: bool,
  code: string,
};

export default ImagesGrid;
